// src/services/websocket-api.ts

const WS_URL = import.meta.env.VITE_WS_URL;

/**
 * Utility functions for working with WebSockets
 */
export const websocketAPI = {
  /**
   * Get the WebSocket URL based on the current environment
   * @param path The WebSocket path (without leading slash)
   * @returns Full WebSocket URL
   */
  getWebSocketUrl: (path: string): string => {
    console.log("🔌 Getting WebSocket URL:", `${WS_URL}/${path}`);
    return `${WS_URL}/${path}`;
  },

  /**
   * Connect to a WebSocket with automatic reconnection
   * @param url WebSocket URL
   * @param options Connection options
   * @returns WebSocket instance with enhanced reconnection capabilities
   */
  connect: (
    url: string,
    options: {
      onOpen?: (event: Event) => void;
      onMessage?: (event: MessageEvent) => void;
      onClose?: (event: CloseEvent) => void;
      onError?: (event: Event) => void;
      onReconnect?: () => void;
      maxReconnectAttempts?: number;
      reconnectInterval?: number;
    } = {}
  ): WebSocket => {
    const ws = new WebSocket(url);

    // Track reconnection attempts
    let reconnectAttempts = 0;
    const maxReconnectAttempts = options.maxReconnectAttempts || 5;
    const reconnectInterval = options.reconnectInterval || 3000;
    let reconnectTimeoutId: number | null = null;

    // Setup event handlers
    ws.onopen = (event) => {
      console.log(`🔌 WebSocket connected: ${url}`);
      reconnectAttempts = 0; // Reset reconnect attempts on successful connection
      if (options.onOpen) options.onOpen(event);
    };

    ws.onmessage = (event) => {
      if (options.onMessage) options.onMessage(event);
    };

    ws.onclose = (event) => {
      console.log(`🔌 WebSocket closed: ${url}`, event.code, event.reason);

      if (options.onClose) options.onClose(event);

      // Only attempt to reconnect on abnormal closure
      if (event.code !== 1000 && event.code !== 1001) {
        if (reconnectAttempts < maxReconnectAttempts) {
          reconnectAttempts++;
          console.log(
            `🔄 Attempting to reconnect (${reconnectAttempts}/${maxReconnectAttempts})...`
          );

          // Attempt to reconnect after delay
          reconnectTimeoutId = window.setTimeout(() => {
            if (options.onReconnect) options.onReconnect();
            websocketAPI.connect(url, options);
          }, reconnectInterval);
        } else {
          console.error(
            `❌ Maximum reconnection attempts (${maxReconnectAttempts}) reached`
          );
        }
      }
    };

    ws.onerror = (event) => {
      console.error(`❌ WebSocket error: ${url}`, event);
      if (options.onError) options.onError(event);
    };

    // Add a custom close method that clears the reconnection timeout
    const originalClose = ws.close;
    ws.close = function (code?: number, reason?: string): void {
      if (reconnectTimeoutId !== null) {
        window.clearTimeout(reconnectTimeoutId);
        reconnectTimeoutId = null;
      }
      originalClose.call(ws, code, reason);
    };

    return ws;
  },
};

export default websocketAPI;
